import { getPredictionPositionSideLabel } from '@src/components/OMS/NewOrder/components/PredictionOrder/predictionPositionDisplay';
import { PredictionLegTag } from '@src/components/PredictionMarkets/styles';
import {
  Position,
  PositionAssetTypeEnum,
  type AgGridSecurityDecorationParams,
  type AssetColumnProps,
  type SizeColumnParams,
  type SizeValue,
} from '@talos/kyoko';
import type { ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import Big from 'big.js';

function isPredictionOutcomePosition(data: unknown): data is Position {
  return data instanceof Position && data.AssetType === PositionAssetTypeEnum.PredictionOutcome;
}

export function getPositionPredictionOutcome(data: unknown) {
  if (!isPredictionOutcomePosition(data)) {
    return undefined;
  }

  return getPredictionPositionSideLabel(data.Amount);
}

export function getPositionInstrumentSuffix({ data }: AgGridSecurityDecorationParams) {
  const outcome = getPositionPredictionOutcome(data);
  if (outcome == null) {
    return undefined;
  }

  return (
    <PredictionLegTag $leg={outcome === 'Yes' ? 'yes' : 'no'} data-testid="prediction-position-outcome">
      {outcome}
    </PredictionLegTag>
  );
}

export function formatPredictionPositionAssetValue(
  displaySymbol: string,
  { data }: Pick<ValueFormatterParams<Position, string>, 'data'>
) {
  const outcome = getPositionPredictionOutcome(data);
  return outcome == null ? displaySymbol : `${displaySymbol} - ${outcome}`;
}

export function transformPositionSizeValue(
  value: SizeValue,
  { data }: Pick<ValueGetterParams<Position>, 'data'>
): SizeValue {
  if (value.value === '' || !isPredictionOutcomePosition(data)) {
    return value;
  }

  return {
    ...value,
    value: Big(value.value).abs().toFixed(),
  };
}

export const positionAssetColumnParams = {
  assetTypeField: 'AssetType',
  colorful: true,
  suffix: getPositionInstrumentSuffix,
} satisfies AssetColumnProps<Position>;

export const searchablePositionAssetColumnParams = {
  ...positionAssetColumnParams,
  includeSecurityIdentifiersInQuickFilter: true,
} satisfies AssetColumnProps<Position>;

export const predictionPositionAssetColumnParams = {
  ...positionAssetColumnParams,
  formatDisplayValue: formatPredictionPositionAssetValue,
} satisfies AssetColumnProps<Position>;

export const positionAmountColumnParams = {
  currencyField: 'NotionalAmountCurrency',
  isValueAlreadyNotional: () => true,
  highlightNegative: true,
  transformValue: transformPositionSizeValue,
  showCurrencyForNullPosition: false,
} satisfies SizeColumnParams<Position>;

export const positionEquivalentAmountColumnParams = {
  currencyField: 'Equivalent.Currency',
  highlightNegative: true,
  transformValue: transformPositionSizeValue,
} satisfies SizeColumnParams<Position>;
